import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { SOCIAL_LINKS } from '../constants';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      // Increased offset to 100px for better clearance under fixed header
      const headerOffset = 100; 
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      setIsOpen(false);
    }
  };

  const navLinks = [
    { label: 'Work', href: '#work' },
    { label: 'Speaking', href: '#speaking' },
    { label: 'Experience', href: '#experience' },
    { label: 'Patents', href: '#patents' },
    { label: 'Bio', href: '#bio' },
    { label: 'Personal', href: '#personal' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 py-4 shadow-sm' : 'bg-white/0 py-6'}`}>
      <div className="container mx-auto px-6 md:px-12 flex justify-between items-center">
        <a href="#" onClick={(e) => handleNavClick(e, '#top')} className="font-serif text-2xl font-bold tracking-tight text-gray-900 hover:text-gray-700 transition-colors">
          Jaclyn Konzelmann
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a 
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-[#FF5E7E] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a 
            href={SOCIAL_LINKS.talks}
            className="text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-[#FF5E7E] transition-colors"
          >
            Talks
          </a>
          <a 
            href={SOCIAL_LINKS.sideProjects}
            className="text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-[#FF5E7E] transition-colors"
          >
            Side Projects
          </a>
          <a 
            href={SOCIAL_LINKS.substack} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-2 bg-gray-900 text-white text-xs font-bold uppercase tracking-widest rounded-full hover:bg-[#FF5E7E] transition-colors shadow-sm transform hover:-translate-y-0.5"
          >
            Blog
          </a>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="md:hidden p-2 text-gray-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-100 md:hidden p-8 flex flex-col gap-6 shadow-2xl animate-in slide-in-from-top duration-300 h-screen">
          {navLinks.map((link) => (
            <a 
              key={link.label}
              href={link.href} 
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-2xl font-serif font-bold text-gray-900 pb-4 border-b border-gray-50 hover:text-[#FF5E7E] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a 
            href={SOCIAL_LINKS.talks}
            className="text-2xl font-serif font-bold text-gray-900 pb-4 border-b border-gray-50 hover:text-[#FF5E7E] transition-colors"
          >
            Talks
          </a>
          <a 
            href={SOCIAL_LINKS.sideProjects}
            className="text-2xl font-serif font-bold text-gray-900 pb-4 border-b border-gray-50 hover:text-[#FF5E7E] transition-colors"
          >
            Side Projects
          </a>
          <a 
            href={SOCIAL_LINKS.substack} 
             target="_blank" 
            rel="noopener noreferrer"
            className="text-xl font-bold text-[#FF5E7E] mt-4"
             onClick={() => setIsOpen(false)}
          >
            Read Blog
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;